import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend);

const PALETTE = ['#0d6efd', '#fd7e14', '#198754', '#6f42c1', '#dc3545', '#ffc107', '#20c997', '#6610f2'];

/**
 * `chart.data` es el arreglo CRUDO de documentos que regresó el pipeline de
 * `generate_chart` (ver `chat.service.js` — el backend NO renderiza imagen, sólo
 * regresa `{ type, collection, description, data }`, ver DUDAS.md D-10.1/D-10.2).
 * Detecta la primera columna string como etiqueta y la primera columna numérica
 * (distinta de la etiqueta) como valor — mismo criterio que `ChartRenderer.jsx` de V1,
 * ahora dibujado con chart.js en vez de barras/SVG hechos a mano.
 */
function deriveLabelValue(rows) {
  const sample = rows[0] || {};
  const keys = Object.keys(sample);
  const labelKey = keys.find((k) => typeof sample[k] === 'string') || keys[0];
  const valueKey = keys.find((k) => typeof sample[k] === 'number' && k !== labelKey) || keys[1] || keys[0];
  return rows
    .filter(Boolean)
    .map((r) => ({ label: String(r[labelKey] ?? '—'), value: Number(r[valueKey]) || 0 }));
}

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
};

/** chart: { type: 'bar'|'line'|'pie', data: object[], description?: string } */
export default function ChartRenderer({ chart }) {
  const data = useMemo(() => (chart?.data?.length ? deriveLabelValue(chart.data) : []), [chart]);

  if (!data.length) return null;

  const labels = data.map((d) => d.label);
  const values = data.map((d) => d.value);

  const chartData = {
    labels,
    datasets: [
      {
        label: chart.description || '',
        data: values,
        backgroundColor: chart.type === 'pie' ? PALETTE : '#2563eb',
        borderColor: chart.type === 'line' ? '#2563eb' : PALETTE,
        borderWidth: chart.type === 'line' ? 2 : 1,
        tension: 0.3,
      },
    ],
  };

  return (
    <div style={{ height: 260 }}>
      {chart.type === 'line' && <Line data={chartData} options={commonOptions} />}
      {chart.type === 'pie' && <Pie data={chartData} options={{ ...commonOptions, plugins: { legend: { display: true, position: 'right' } } }} />}
      {(!chart.type || chart.type === 'bar') && <Bar data={chartData} options={commonOptions} />}
    </div>
  );
}
